// This component handles all of the top-level routes (/*)
// Child routes (/**/*) should preferably be handled by the top-level routes' components and not included here

import React, { lazy, Suspense, useContext } from 'react';
import { Route, Switch } from 'react-router-dom';

import { AdminRouter } from 'screens/admin/AdminRouter';
import { SubmitRouter } from 'screens/submit/SubmitRouter';
import { QueryRouter } from 'screens/query/QueryRouter';
import { ReportsRouter } from 'screens/reports/ReportsRouter';
import { AccountRouter } from 'screens/account/AccountRouter';
import { AuthContext } from 'appRoot/auth/AuthContext';
import { Login } from 'components/auth/Login';
import { LoadingScreen } from 'util-components/screens/LoadingScreen';
import { ChangeTemporaryPasswordScreen } from 'screens/account/changeTemporaryPasswordScreen/ChangeTemporaryPasswordScreen';
import { MfaSetup } from 'components/auth/MfaSetup';
import { MfaChallenge } from 'components/auth/MfaChallenge';

// Using lazy here splits out each of these into a separate chunk (code splitting)
// this prevents the initial load time from becoming too long by reducing the size of the root chunk
// Without code splitting, the user will have to download the entire application at once rather than just the screen they want
const HomeScreen = lazy(() => import('screens/HomeScreen'));
const NotFoundScreen = lazy(() => import('screens/NotFoundScreen'));
const SiteMapScreen = lazy(() => import('screens/SiteMapScreen'));

const AuthRouter = () => {
const context = useContext(AuthContext);

  if (!context) {
    console.error("AuthContext is null. Check AuthProvider placement in App.js");
    return <LoadingScreen />;
  }

  const { authState } = context;

  if (authState === 'loading') {
     return <LoadingScreen />;
  }

  // Not logged in → show login ONLY
  if ( authState === null || authState === 'loggedOut') {
    return <Login />;
  }

  // Auth flows
  if (authState === 'mfaChallenge') {
    return <MfaChallenge />;
  }
  if (authState === 'mfaSetupRequired') {
    return <MfaSetup />;
  }
  if (authState === 'changePasswordRequired') {
    return <ChangeTemporaryPasswordScreen />;
  }

  // Logged in → show app routes
  if (authState === 'loggedIn') {
    return (
      <div className="auth-router-wrapper">
        <Suspense fallback={<LoadingScreen />}>
          <Switch>
            <Route path="/" exact component={HomeScreen} />
            <Route path="/admin" component={AdminRouter} />
            <Route path="/submit" component={SubmitRouter} />
            <Route path="/query" component={QueryRouter} />
            <Route path="/reports" component={ReportsRouter} />
            <Route path="/account" component={AccountRouter} />
            <Route path="/sitemap" exact component={SiteMapScreen} />
            <Route component={NotFoundScreen} />
          </Switch>
        </Suspense>
      </div>
    );
  }

  // Fallback
  return <LoadingScreen />;
};

export { AuthRouter };
